import React, {useEffect, useId, useMemo, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {Background, Controls, ReactFlow} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {Button} from "primereact/button";
import {Checkbox} from "primereact/checkbox";
import {ConfirmDialog, confirmDialog} from "primereact/confirmdialog";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Message} from "primereact/message";
import {MultiSelect} from "primereact/multiselect";
import {OverlayPanel} from "primereact/overlaypanel";
import {ProgressSpinner} from "primereact/progressspinner";
import {Steps} from "primereact/steps";
import {Tag} from "primereact/tag";
import {useTranslate} from "../useTranslate";
import {dateToPersian} from "../utils";
import Avatar from "./avatar";
import BrandLogo from "./brand-logo";
import ClearableDropdown from "./clearable-dropdown";
import PrimeDatePicker from "./prime-date-picker";
import {aiActionLabel} from "./ai-action-label";
import SelectCycle from "../../pages/goals/components/select-cycle";

const STEPS = [
    ["ai.creation.step.type", "pi pi-th-large"],
    ["ai.creation.step.information", "pi pi-info-circle"],
    ["ai.creation.step.members", "pi pi-users"],
    ["ai.creation.step.mode", "pi pi-sparkles"],
];

const ENTITY_ICONS = {
    goal: "pi pi-flag",
    project: "pi pi-folder",
    deliverable: "pi pi-box",
    task: "pi pi-check-circle",
    team: "pi pi-users",
};

const ENTITY_PERMISSIONS = {
    goal: "create-new-goals",
    project: "create-new-projects",
    deliverable: "canManageTasks",
    task: "canManageTasks",
    team: "manage-teams",
};

const DROPDOWN_PT = {
    input: {className: "text-xs flex align-items-center"},
    item: {className: "text-xs p-2 w-full"},
    emptyMessage: {className: "text-xs p-3"},
    filterContainer: {className: "rtl"},
    filterInput: {className: "w-full text-xs text-right", dir: "rtl"},
};

const MULTISELECT_PT = {
    root: () => ({className: "w-full border-none shadow-none align-items-stretch"}),
    wrapper: () => ({className: "scrollbar-hide"}),
    label: () => ({
        className: "flex flex-wrap align-items-center gap-1",
        style: {whiteSpace: "normal", overflow: "visible"},
    }),
    labelContainer: () => ({
        className: "align-items-center flex p-2",
        style: {overflow: "visible", minHeight: "3rem"},
    }),
    trigger: () => ({className: "p-3 align-self-start"}),
    filterInput: () => ({className: "p-0"}),
    header: () => ({className: "p-2 flex-row-reverse"}),
    item: () => ({className: "flex-row-reverse justify-content-between p-2"}),
};

const PERSIAN_TEXT = /[\u0600-\u06ff]/;

const clone = (value) => JSON.parse(JSON.stringify(value || {}));

function clientReference(operation) {
    return operation?.preconditions?.clientReference || "";
}

function relationDocumentId(value) {
    if (Array.isArray(value)) return relationDocumentId(value[0]);
    return value?.documentId || value?.id || "";
}

function normalizeDate(value) {
    if (!value) return null;
    if (String(value).includes("T")) return value;
    return `${value}T23:59:59.000Z`;
}

function unixDate(value) {
    const milliseconds = Date.parse(value);
    return Number.isNaN(milliseconds) ? undefined : Math.floor(milliseconds / 1000);
}

function displayDate(value) {
    if (!value) return "—";
    try {
        return dateToPersian(value, "YYYY/MM/DD");
    } catch {
        return "—";
    }
}

function titleField(entityType) {
    return entityType === "team" ? "name" : "title";
}

function operationTitle(operation) {
    return operation?.payload?.[titleField(operation?.entityType)] || "";
}

function rootOperation(operations) {
    return operations.find((operation) => (
        ["goal", "project"].includes(operation.entityType)
        && clientReference(operation).startsWith("root-")
    )) || operations.find((operation) => ["goal", "project"].includes(operation.entityType));
}

function memberKey(membership) {
    return String(membership?.documentId || membership?.id || "");
}

function memberProfile(membership) {
    return membership?.member || membership;
}

function relationFor(record) {
    const documentId = record?.documentId;
    return documentId ? {documentId} : undefined;
}

function sameRecord(left, right) {
    if (!left || !right) return false;
    return String(left.documentId || left.id) === String(right.documentId || right.id);
}

function hasReference(value, reference) {
    if (Array.isArray(value)) return value.some((item) => hasReference(item, reference));
    if (!value || typeof value !== "object") return false;
    if (value.$proposalRef === reference) return true;
    return Object.values(value).some((item) => hasReference(item, reference));
}

function removeReference(value, reference) {
    if (Array.isArray(value)) {
        return value
            .filter((item) => !hasReference(item, reference))
            .map((item) => removeReference(item, reference));
    }
    if (!value || typeof value !== "object") return value;
    if (value.$proposalRef === reference) return undefined;
    return Object.fromEntries(
        Object.entries(value)
            .map(([key, item]) => [key, removeReference(item, reference)])
            .filter(([, item]) => item !== undefined),
    );
}

function defaultBaseInfo(owner = null) {
    return {
        title: "",
        description: "",
        rationale: "",
        dueDate: null,
        startAt: new Date().toISOString(),
        owner,
        members: [],
        parentGoal: null,
        relatedGoal: null,
        team: null,
        cycle: null,
    };
}

function compactRelation(record) {
    if (!record) return null;
    if (record.documentId) return {documentId: record.documentId};
    if (record.id !== undefined && record.id !== null) return {id: record.id};
    return null;
}

function compactTask(task) {
    return {
        id: task.id,
        title: task.title || "",
        description: task.description || "",
        dueDate: task.dueDate || null,
        assignee: compactRelation(task.assignee),
    };
}

function compactMilestone(milestone) {
    return {
        id: milestone.id,
        title: milestone.title || "",
        description: milestone.description || "",
        dueDate: milestone.dueDate || null,
        tasks: (milestone.tasks || []).map(compactTask),
    };
}

function compactGoalChild(child) {
    return {
        id: child.id,
        type: child.type,
        title: child.title || "",
        description: child.description || "",
        milestones: (child.milestones || []).map(compactMilestone),
    };
}

function compactManualTeam(teamItem) {
    return {
        id: teamItem.id,
        name: teamItem.name || "",
        description: teamItem.description || "",
        members: (teamItem.members || []).map(compactRelation).filter(Boolean),
    };
}

function compactDraft(draft) {
    return {
        id: draft?.id,
        entityType: draft?.entityType,
        actionKey: draft?.actionKey,
        actionCount: draft?.actionCount,
        title: draft?.title,
        summary: draft?.summary,
        confidence: draft?.confidence,
        contextHash: draft?.contextHash,
        generated: draft?.generated,
        operations: Array.isArray(draft?.operations) ? draft.operations : [],
        staffingRecommendations: Array.isArray(draft?.staffingRecommendations)
            ? draft.staffingRecommendations
            : [],
        memberOptions: [],
        teamOptions: [],
    };
}

function compactWizardSnapshot(snapshot) {
    const baseInfo = snapshot.baseInfo || {};
    return {
        version: 2,
        activeStep: Number(snapshot.activeStep) || 0,
        maxVisitedStep: Number(snapshot.maxVisitedStep) || 0,
        creationType: snapshot.creationType,
        creationMode: snapshot.creationMode,
        generatedSignature: String(snapshot.generatedSignature || "").length <= 100000
            ? snapshot.generatedSignature
            : "",
        savedAt: snapshot.savedAt || new Date().toISOString(),
        baseInfo: {
            title: baseInfo.title || "",
            description: baseInfo.description || "",
            rationale: baseInfo.rationale || "",
            dueDate: baseInfo.dueDate || null,
            startAt: baseInfo.startAt || null,
            owner: compactRelation(baseInfo.owner),
            members: (baseInfo.members || []).map(compactRelation).filter(Boolean),
            parentGoal: compactRelation(baseInfo.parentGoal),
            relatedGoal: compactRelation(baseInfo.relatedGoal),
            team: compactRelation(baseInfo.team),
            cycle: baseInfo.cycle || null,
        },
        goalChildren: (snapshot.goalChildren || []).map(compactGoalChild),
        milestones: (snapshot.milestones || []).map(compactMilestone),
        manualTeams: (snapshot.manualTeams || []).map(compactManualTeam),
        draft: compactDraft(snapshot.draft),
    };
}

function isStorageQuotaError(error) {
    return error?.name === "QuotaExceededError"
        || error?.name === "NS_ERROR_DOM_QUOTA_REACHED"
        || error?.code === 22
        || error?.code === 1014;
}

function removeLocalDraft(storageKey) {
    try {
        window.localStorage.removeItem(storageKey);
        return true;
    } catch {
        return false;
    }
}

function persistLocalDraft(storageKey, snapshot) {
    const compactSnapshot = compactWizardSnapshot(snapshot);
    const serialized = JSON.stringify(compactSnapshot);
    try {
        window.localStorage.setItem(storageKey, serialized);
        return true;
    } catch (error) {
        if (!isStorageQuotaError(error)) return false;
    }

    try {
        window.localStorage.removeItem(storageKey);
        window.localStorage.setItem(storageKey, serialized);
        return true;
    } catch (error) {
        if (!isStorageQuotaError(error)) return false;
    }

    const prefix = "zofino:ai-creation-draft:";
    try {
        const draftKeys = Array.from(
            {length: window.localStorage.length},
            (_, index) => window.localStorage.key(index),
        ).filter((key) => key?.startsWith(prefix) && key !== storageKey);
        for (const key of draftKeys) {
            const previous = window.localStorage.getItem(key);
            if (!previous) continue;
            try {
                const compactPrevious = JSON.stringify(
                    compactWizardSnapshot(JSON.parse(previous)),
                );
                window.localStorage.removeItem(key);
                window.localStorage.setItem(key, compactPrevious);
            } catch {
                try {
                    window.localStorage.setItem(key, previous);
                } catch {
                    // Never leak a storage exception into the wizard.
                }
            }
        }
        window.localStorage.setItem(storageKey, serialized);
        return true;
    } catch {
        return false;
    }
}

function wizardSignature(creationType, baseInfo, goalChildren = [], milestones = [], manualTeams = []) {
    return JSON.stringify({
        creationType,
        title: baseInfo.title.trim(),
        description: baseInfo.description.trim(),
        rationale: baseInfo.rationale.trim(),
        dueDate: baseInfo.dueDate,
        startAt: baseInfo.startAt,
        owner: memberKey(baseInfo.owner),
        members: (baseInfo.members || []).map(memberKey).sort(),
        parentGoal: baseInfo.parentGoal?.documentId || "",
        relatedGoal: baseInfo.relatedGoal?.documentId || "",
        team: baseInfo.team?.documentId || "",
        cycle: baseInfo.cycle || null,
        goalChildren: goalChildren.map(compactGoalChild),
        milestones: milestones.map(compactMilestone),
        manualTeams: manualTeams.map(compactManualTeam),
    });
}

function graphElements(operations, translate) {
    const references = new Map(operations.map((operation, index) => [
        clientReference(operation) || `operation-${index}`,
        {operation, index},
    ]));
    const hierarchyParents = (operation) => {
        const payload = operation?.payload || {};
        const values = operation.entityType === "goal"
            ? [payload.parentGoal]
            : operation.entityType === "project"
                ? [payload.parentProjects, payload.goals]
                : operation.entityType === "deliverable"
                    ? [payload.project]
                    : operation.entityType === "task"
                        ? [payload.deliverables, payload.projects, payload.parentTask]
                        : [];
        const found = [];
        const visit = (value) => {
            if (Array.isArray(value)) return value.forEach(visit);
            if (!value || typeof value !== "object") return;
            if (value.$proposalRef && references.has(value.$proposalRef)) found.push(value.$proposalRef);
        };
        values.forEach(visit);
        return found;
    };
    const depthCache = new Map();
    const depthFor = (reference, trail = new Set()) => {
        if (depthCache.has(reference)) return depthCache.get(reference);
        if (trail.has(reference)) return 0;
        const item = references.get(reference);
        if (!item) return 0;
        const parents = hierarchyParents(item.operation);
        if (!parents.length) {
            const root = clientReference(item.operation).startsWith("root-");
            const depth = root ? 0 : 1;
            depthCache.set(reference, depth);
            return depth;
        }
        const nextTrail = new Set(trail);
        nextTrail.add(reference);
        const depth = Math.max(...parents.map((parent) => depthFor(parent, nextTrail))) + 1;
        depthCache.set(reference, depth);
        return depth;
    };
    const lanes = new Map();
    for (const [reference, item] of references) {
        const depth = depthFor(reference);
        if (!lanes.has(depth)) lanes.set(depth, []);
        lanes.get(depth).push(item);
    }
    const positions = new Map();
    let nextY = 30;
    for (const depth of [...lanes.keys()].sort((left, right) => left - right)) {
        const laneOperations = lanes.get(depth);
        const laneY = nextY;
        laneOperations.forEach(({index}, laneIndex) => {
            positions.set(index, {
                x: 35 + (laneIndex % 4) * 245,
                y: laneY + Math.floor(laneIndex / 4) * 120,
            });
        });
        nextY += Math.ceil(laneOperations.length / 4) * 120 + 45;
    }
    const nodes = operations.map((operation, index) => {
        return {
            id: clientReference(operation) || `operation-${index}`,
            position: positions.get(index) || {x: 35, y: nextY},
            data: {
                label: `${translate(`ai.action.entity.${operation.entityType}`)}: ${operationTitle(operation) || translate("ai.actionWizard.untitled")}`,
            },
            className: `surface-card border-${clientReference(operation).startsWith("root-") ? "2 border-primary" : "1 border-200"} border-round-xl text-xs p-2`,
        };
    });
    const nodeIds = new Set(nodes.map((node) => node.id));
    const edges = [];
    operations.forEach((operation, index) => {
        const target = clientReference(operation) || `operation-${index}`;
        const serialized = JSON.stringify(operation.payload || {});
        for (const source of nodeIds) {
            if (source !== target && serialized.includes(`"$proposalRef":"${source}"`)) {
                edges.push({
                    id: `${source}-${target}`,
                    source,
                    target,
                    animated: operation.entityType === "task",
                });
            }
        }
    });
    return {nodes, edges};
}

function buildManualOperations({
    creationType,
    baseInfo,
    goalChildren,
    milestones,
    manualTeams,
    creatorUser,
    allowedTypes,
    allowedEntityTypes,
}) {
    if (!allowedTypes.includes(creationType)) return [];
    const rootReference = `root-${creationType}`;
    const owner = relationFor(baseInfo.owner);
    const team = relationFor(baseInfo.team);
    const members = (baseInfo.members || []).map(relationFor).filter(Boolean);
    const creator = relationFor(creatorUser);
    const createdTeams = (manualTeams || []).filter((item) => item.name?.trim());
    const defaultTeam = team || (
        createdTeams.length === 1 ? {$proposalRef: createdTeams[0].id} : undefined
    );
    const rootPayload = creationType === "goal"
        ? {
            title: baseInfo.title.trim(),
            description: baseInfo.description.trim(),
            why: baseInfo.rationale.trim(),
            goalStatus: "on-way",
            dueDate: normalizeDate(baseInfo.dueDate),
            progressSource: "auto",
            ...(baseInfo.cycle ? {cycle: baseInfo.cycle} : {}),
            ...(owner ? {owner} : {}),
            ...(defaultTeam ? {team: defaultTeam} : {}),
            ...(baseInfo.parentGoal ? {parentGoal: relationFor(baseInfo.parentGoal)} : {}),
        }
        : {
            title: baseInfo.title.trim(),
            description: baseInfo.description.trim(),
            hypothesis: baseInfo.rationale.trim(),
            projectStatus: "on-way",
            startAt: normalizeDate(baseInfo.startAt),
            dueDate: normalizeDate(baseInfo.dueDate),
            reminderRhythm: "weekly",
            ...(owner ? {owner} : {}),
            ...(defaultTeam ? {team: defaultTeam} : {}),
            ...(members.length ? {members} : {}),
            ...(baseInfo.relatedGoal ? {goals: [relationFor(baseInfo.relatedGoal)]} : {}),
        };
    const operations = createdTeams.map((item, index) => ({
        operationType: "create_team",
        entityType: "team",
        requiredPermission: ENTITY_PERMISSIONS.team,
        preconditions: {clientReference: item.id},
        payload: {
            name: item.name.trim(),
            slug: `ai-manual-team-${Date.now()}-${index + 1}`,
            description: item.description?.trim() || "",
            notifications: true,
            members: (item.members || []).map(relationFor).filter(Boolean),
            ...(owner ? {owner} : {}),
        },
    }));
    operations.push({
        operationType: "create",
        entityType: creationType,
        requiredPermission: ENTITY_PERMISSIONS[creationType],
        preconditions: {clientReference: rootReference},
        payload: rootPayload,
    });

    const addMilestones = (projectReference, projectMilestones) => {
        if (!allowedEntityTypes.includes("deliverable")) return;
        for (const milestone of projectMilestones || []) {
            operations.push({
                operationType: "create",
                entityType: "deliverable",
                requiredPermission: ENTITY_PERMISSIONS.deliverable,
                preconditions: {clientReference: milestone.id},
                payload: {
                    title: milestone.title.trim(),
                    description: milestone.description?.trim() || "",
                    deliverableStatus: "todo",
                    startAt: normalizeDate(baseInfo.startAt),
                    doneAt: normalizeDate(milestone.dueDate || baseInfo.dueDate),
                    impactWeight: 70,
                    project: {$proposalRef: projectReference},
                },
            });
            if (!allowedEntityTypes.includes("task")) continue;
            for (const task of milestone.tasks || []) {
                operations.push({
                    operationType: "create",
                    entityType: "task",
                    requiredPermission: ENTITY_PERMISSIONS.task,
                    preconditions: {clientReference: task.id},
                    payload: {
                        title: task.title.trim(),
                        description: task.description?.trim() || "",
                        taskStatus: "todo",
                        startAt: normalizeDate(baseInfo.startAt),
                        dueDate: normalizeDate(task.dueDate || milestone.dueDate || baseInfo.dueDate),
                        scheduleType: "end-based",
                        projects: [{$proposalRef: projectReference}],
                        deliverables: [{$proposalRef: milestone.id}],
                        extra: {type: "ai-generated"},
                        ...(creator ? {creatorUser: creator} : {}),
                        ...(task.assignee ? {assignee: relationFor(task.assignee)} : {}),
                    },
                });
            }
        }
    };

    if (creationType === "goal") {
        for (const child of goalChildren) {
            if (!allowedTypes.includes(child.type)) continue;
            operations.push({
                operationType: "create",
                entityType: child.type,
                requiredPermission: ENTITY_PERMISSIONS[child.type],
                preconditions: {clientReference: child.id},
                payload: child.type === "goal"
                    ? {
                        title: child.title.trim(),
                        description: child.description.trim(),
                        why: "",
                        goalStatus: "on-way",
                        dueDate: normalizeDate(baseInfo.dueDate),
                        progressSource: "auto",
                        parentGoal: {$proposalRef: rootReference},
                        ...(owner ? {owner} : {}),
                        ...(defaultTeam ? {team: defaultTeam} : {}),
                    }
                    : {
                        title: child.title.trim(),
                        description: child.description.trim(),
                        hypothesis: "",
                        projectStatus: "on-way",
                        startAt: normalizeDate(baseInfo.startAt),
                        dueDate: normalizeDate(baseInfo.dueDate),
                        reminderRhythm: "weekly",
                        goals: [{$proposalRef: rootReference}],
                        ...(owner ? {owner} : {}),
                        ...(defaultTeam ? {team: defaultTeam} : {}),
                        ...(members.length ? {members} : {}),
                    },
            });
            if (child.type === "project") addMilestones(child.id, child.milestones);
        }
    }

    if (creationType === "project") addMilestones(rootReference, milestones);
    return operations;
}

function mergeBaseIntoOperations(operations, creationType, baseInfo) {
    const nextOperations = clone(operations);
    const rootIndex = nextOperations.findIndex((operation) => operation === rootOperation(nextOperations));
    if (rootIndex < 0) return nextOperations;
    const root = nextOperations[rootIndex];
    const owner = relationFor(baseInfo.owner);
    const team = relationFor(baseInfo.team);
    const members = (baseInfo.members || []).map(relationFor).filter(Boolean);
    const payload = {
        ...root.payload,
        title: baseInfo.title.trim() || root.payload?.title,
        description: baseInfo.description.trim() || root.payload?.description,
        dueDate: normalizeDate(baseInfo.dueDate) || root.payload?.dueDate,
        ...(owner ? {owner} : {}),
        ...(team ? {team} : {}),
    };
    if (creationType === "goal") {
        payload.why = baseInfo.rationale.trim() || root.payload?.why || "";
        if (baseInfo.cycle) payload.cycle = baseInfo.cycle;
        else delete payload.cycle;
        if (baseInfo.parentGoal) payload.parentGoal = relationFor(baseInfo.parentGoal);
        else delete payload.parentGoal;
    } else {
        payload.hypothesis = baseInfo.rationale.trim() || root.payload?.hypothesis || "";
        payload.startAt = normalizeDate(baseInfo.startAt) || root.payload?.startAt;
        if (members.length) payload.members = members;
        else delete payload.members;
        if (baseInfo.relatedGoal) payload.goals = [relationFor(baseInfo.relatedGoal)];
        else if (!root.payload?.goals?.length) delete payload.goals;
    }
    nextOperations[rootIndex] = {...root, payload};
    if (members.length) {
        return nextOperations.map((operation, index) => (
            index !== rootIndex && operation.entityType === "project"
                ? {...operation, payload: {...operation.payload, members}}
                : operation
        ));
    }
    return nextOperations;
}

function pruneUnsupportedOperations(operations, creationType, allowedTypes, allowedEntityTypes) {
    const supported = new Set([...allowedTypes, ...allowedEntityTypes, creationType]);
    const removedReferences = operations
        .filter((operation) => !supported.has(operation.entityType))
        .map(clientReference)
        .filter(Boolean);
    return operations
        .filter((operation) => supported.has(operation.entityType))
        .map((operation) => ({
            ...operation,
            payload: removedReferences.reduce(
                (payload, reference) => removeReference(payload, reference),
                operation.payload,
            ),
        }));
}

function proposalReferences(value, result = new Set()) {
    if (Array.isArray(value)) {
        value.forEach((item) => proposalReferences(item, result));
    } else if (value && typeof value === "object") {
        if (value.$proposalRef) result.add(value.$proposalRef);
        Object.values(value).forEach((item) => proposalReferences(item, result));
    }
    return result;
}

function sortOperationsByDependencies(operations) {
    const pending = [...operations];
    const sorted = [];
    const completed = new Set();
    while (pending.length) {
        const index = pending.findIndex((operation) => (
            [...proposalReferences(operation.payload)].every((reference) => completed.has(reference))
        ));
        const nextIndex = index < 0 ? 0 : index;
        const [operation] = pending.splice(nextIndex, 1);
        sorted.push(operation);
        const reference = clientReference(operation);
        if (reference) completed.add(reference);
    }
    return sorted;
}

function mergeManualOperations(generatedOperations, manualOperations, creationType) {
    const rootReference = `root-${creationType}`;
    const manualAdditions = manualOperations.filter(
        (operation) => (
            clientReference(operation) !== rootReference
            && operationTitle(operation).trim()
        ),
    );
    const generatedReferences = new Set(generatedOperations.map(clientReference).filter(Boolean));
    let uniqueManualAdditions = manualAdditions.filter(
        (operation) => !generatedReferences.has(clientReference(operation)),
    );
    const available = new Set([...generatedReferences, rootReference]);
    let changed = true;
    while (changed) {
        changed = false;
        for (const operation of uniqueManualAdditions) {
            const reference = clientReference(operation);
            if (
                !available.has(reference)
                && [...proposalReferences(operation.payload)].every((dependency) => available.has(dependency))
            ) {
                available.add(reference);
                changed = true;
            }
        }
    }
    uniqueManualAdditions = uniqueManualAdditions.filter(
        (operation) => available.has(clientReference(operation)),
    );
    return sortOperationsByDependencies([...generatedOperations, ...uniqueManualAdditions]);
}

function draftStorageKey(workspaceMembership, draftContext, initialType) {
    const workspaceId = workspaceMembership?.workspace?.documentId
        || workspaceMembership?.workspace?.id
        || "workspace";
    const userId = workspaceMembership?.documentId || workspaceMembership?.id || "member";
    const page = draftContext?.pageType || "creation";
    const entity = draftContext?.entityDocumentId
        || draftContext?.documentId
        || draftContext?.entityId
        || "list";
    return `zofino:ai-creation-draft:v2:${workspaceId}:${userId}:${page}:${entity}:${initialType}`;
}

function TypeCard({type, selected, disabled, translate, onClick}) {
    return (
        <Button
            type="button"
            outlined={!selected}
            disabled={disabled}
            aria-pressed={selected}
            className={`ai-creation-type-card text-xs ${selected ? "is-selected" : ""}`}
            onClick={onClick}
        >
            <span className={`ai-creation-type-icon ${selected ? "bg-primary text-primary-contrast" : "surface-100 text-500"}`}>
                <i className={`${ENTITY_ICONS[type]} text-2xl`}/>
            </span>
            <span className="font-bold text-sm">{translate(`ai.creation.type.${type}`)}</span>
            <span className="text-xs text-500 line-height-3 font-normal">
                {translate(`ai.creation.type.${type}Description`)}
            </span>
        </Button>
    );
}

function CyclePicker({value, translate, onChange}) {
    const panel = useRef(null);
    return (
        <div className="flex align-items-center gap-2">
            <Button
                type="button"
                outlined
                className="flex-1 justify-content-between text-xs gap-2"
                icon="pi pi-calendar"
                label={value ? `${translate(value.range)} ${value.year}` : translate("select-cycle")}
                onClick={(event) => panel.current?.toggle(event)}
            />
            <Button
                type="button"
                outlined
                severity="danger"
                icon="pi pi-trash"
                aria-label={translate("clear")}
                disabled={!value}
                onClick={() => onChange(null)}
            />
            <OverlayPanel
                ref={panel}
                pt={{
                    root: {className: "p-0 rtl"},
                    content: {className: "p-0"},
                }}
            >
                <SelectCycle
                    value={value}
                    onChange={onChange}
                    onHide={() => panel.current?.hide()}
                />
            </OverlayPanel>
        </div>
    );
}

function AiActionWizard({
    visible,
    suggestion,
    initialType = "project",
    allowedTypes = ["project", "goal"],
    allowedEntityTypes = ["project", "deliverable", "task", "team"],
    loading,
    onHide,
    onGenerate,
    onSubmit,
    draftContext,
}) {
    const translate = useTranslate();
    const dialogInstanceId = useId().replace(/:/g, "");
    const closeDialogGroup = `ai-creation-close-${dialogInstanceId}`;
    const clearDialogGroup = `ai-creation-clear-${dialogInstanceId}`;
    const initializedKey = useRef(null);
    const workspaceMembershipData = useSelector((state) => state.app.workspaceMembershipData);
    const workspace = workspaceMembershipData?.workspace;
    const workspaceStatus = workspace?.workspaceStatus || "active";
    const workspaceMemberships = useMemo(
        () => (workspace?.workspace_memberships || []).filter(
            (membership) => !membership?.memberShipStatus || membership.memberShipStatus === "joined",
        ),
        [workspace?.workspace_memberships],
    );
    const goals = useMemo(() => workspace?.goals || [], [workspace?.goals]);
    const teams = useMemo(() => workspace?.teams || [], [workspace?.teams]);

    const [activeStep, setActiveStep] = useState(0);
    const [maxVisitedStep, setMaxVisitedStep] = useState(0);
    const [creationType, setCreationType] = useState(initialType);
    const [baseInfo, setBaseInfo] = useState(() => defaultBaseInfo(workspaceMembershipData));
    const [creationMode, setCreationMode] = useState("ai");
    const [goalChildren, setGoalChildren] = useState([]);
    const [milestones, setMilestones] = useState([]);
    const [manualTeams, setManualTeams] = useState([]);
    const [draft, setDraft] = useState({
        operations: [],
        memberOptions: [],
        teamOptions: [],
        staffingRecommendations: [],
    });
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [storageWarning, setStorageWarning] = useState(false);
    const [errors, setErrors] = useState({});
    const [generatedSignature, setGeneratedSignature] = useState("");
    const [memberQuery, setMemberQuery] = useState("");
    const [dirty, setDirty] = useState(false);
    const [mobilePane, setMobilePane] = useState("form");
    const [proposalOnly, setProposalOnly] = useState(false);
    const hydrated = useRef(false);
    const externallySeeded = Boolean(suggestion?.id || suggestion?.contextHash);
    const storageKey = useMemo(
        () => draftStorageKey(workspaceMembershipData, draftContext, initialType),
        [draftContext, initialType, workspaceMembershipData],
    );

    const membershipByDocumentId = useMemo(
        () => new Map(workspaceMemberships.map((membership) => [memberKey(membership), membership])),
        [workspaceMemberships],
    );

    useEffect(() => {
        if (!visible) {
            initializedKey.current = null;
            hydrated.current = false;
            return;
        }
        const key = String(suggestion?.id || suggestion?.contextHash || "new");
        if (initializedKey.current === key) return;
        initializedKey.current = key;

        const incomingOperations = clone(suggestion?.operations || []);
        const incomingRoot = rootOperation(incomingOperations);
        const findMembership = (value) => (
            membershipByDocumentId.get(String(relationDocumentId(value))) || null
        );
        const findGoal = (value) => (
            goals.find((goal) => String(goal.documentId) === String(relationDocumentId(value))) || null
        );
        const findTeam = (value) => (
            teams.find((team) => String(team.documentId) === String(relationDocumentId(value))) || null
        );
        const preferredType = incomingRoot?.entityType
            || (suggestion?.entityType === "goal" ? "goal" : initialType)
            || "project";
        const nextType = allowedTypes.includes(preferredType)
            ? preferredType
            : (allowedTypes[0] || preferredType);
        const rootPayload = incomingRoot?.payload || {};
        const nextMembers = (rootPayload.members || [])
            .map(findMembership)
            .filter(Boolean);
        const nextBaseInfo = {
            ...defaultBaseInfo(workspaceMembershipData),
            title: rootPayload.title || (
                ["goal", "project"].includes(suggestion?.entityType) ? suggestion?.title : ""
            ) || "",
            description: rootPayload.description || suggestion?.summary || "",
            rationale: nextType === "goal"
                ? (rootPayload.why || "")
                : (rootPayload.hypothesis || ""),
            dueDate: rootPayload.dueDate || rootPayload.doneAt || null,
            startAt: rootPayload.startAt || new Date().toISOString(),
            owner: findMembership(rootPayload.owner) || workspaceMembershipData || null,
            members: nextMembers,
            parentGoal: findGoal(rootPayload.parentGoal),
            relatedGoal: findGoal(rootPayload.goals),
            team: findTeam(rootPayload.team),
            cycle: rootPayload.cycle || null,
        };
        const standaloneProposal = incomingOperations.length > 0 && !incomingRoot;
        if (!incomingOperations.length && !suggestion?.id && !suggestion?.contextHash) {
            try {
                const saved = JSON.parse(window.localStorage.getItem(storageKey) || "null");
                if (saved?.version === 2 && allowedTypes.includes(saved.creationType)) {
                    const savedBaseInfo = saved.baseInfo || {};
                    const restoreMilestones = (items) => (Array.isArray(items) ? items : []).map(
                        (milestone) => ({
                            ...milestone,
                            tasks: (milestone.tasks || []).map((task) => ({
                                ...task,
                                assignee: findMembership(task.assignee),
                            })),
                        }),
                    );
                    setCreationType(saved.creationType);
                    setBaseInfo({
                        ...defaultBaseInfo(workspaceMembershipData),
                        ...savedBaseInfo,
                        owner: findMembership(savedBaseInfo.owner) || workspaceMembershipData || null,
                        members: (savedBaseInfo.members || []).map(findMembership).filter(Boolean),
                        parentGoal: findGoal(savedBaseInfo.parentGoal),
                        relatedGoal: findGoal(savedBaseInfo.relatedGoal),
                        team: findTeam(savedBaseInfo.team),
                    });
                    setCreationMode(saved.creationMode === "manual" ? "manual" : "ai");
                    setGoalChildren((Array.isArray(saved.goalChildren) ? saved.goalChildren : []).map(
                        (child) => ({...child, milestones: restoreMilestones(child.milestones)}),
                    ));
                    setMilestones(restoreMilestones(saved.milestones));
                    setManualTeams((Array.isArray(saved.manualTeams) ? saved.manualTeams : []).map(
                        (teamItem) => ({
                            ...teamItem,
                            members: (teamItem.members || []).map(findMembership).filter(Boolean),
                        }),
                    ));
                    setDraft(saved.draft || {
                        operations: [],
                        memberOptions: [],
                        teamOptions: [],
                        staffingRecommendations: [],
                    });
                    setProposalOnly(false);
                    setActiveStep(Math.min(Number(saved.activeStep) || 0, STEPS.length - 1));
                    setMaxVisitedStep(Math.min(Number(saved.maxVisitedStep) || 0, STEPS.length - 1));
                    setGeneratedSignature(saved.generatedSignature || "");
                    setMemberQuery("");
                    setMobilePane("form");
                    setErrors({});
                    setError(null);
                    setStorageWarning(false);
                    setDirty(true);
                    hydrated.current = true;
                    return;
                }
            } catch {
                try {
                    window.localStorage.removeItem(storageKey);
                } catch {
                    // Storage may be unavailable; the wizard can still continue in memory.
                }
            }
        }
        setCreationType(nextType);
        setBaseInfo(nextBaseInfo);
        setCreationMode("ai");
        setGoalChildren([]);
        setMilestones([]);
        setManualTeams([]);
        setDraft({
            ...clone(suggestion || {}),
            operations: incomingOperations,
            memberOptions: clone(suggestion?.memberOptions || []),
            teamOptions: clone(suggestion?.teamOptions || []),
            staffingRecommendations: clone(suggestion?.staffingRecommendations || []),
        });
        setProposalOnly(standaloneProposal);
        setActiveStep(incomingOperations.length ? 3 : 0);
        setMaxVisitedStep(incomingOperations.length ? 3 : 0);
        setGeneratedSignature(incomingOperations.length
            ? wizardSignature(nextType, nextBaseInfo)
            : "");
        setMemberQuery("");
        setMobilePane("form");
        setErrors({});
        setError(null);
        setStorageWarning(false);
        setDirty(false);
        hydrated.current = true;
    }, [
        allowedTypes,
        initialType,
        membershipByDocumentId,
        suggestion,
        visible,
        workspaceMembershipData,
        goals,
        teams,
        storageKey,
    ]);

    useEffect(() => {
        if (!visible || !hydrated.current || proposalOnly || externallySeeded) return;
        const timer = window.setTimeout(() => {
            const persisted = persistLocalDraft(storageKey, {
                version: 2,
                activeStep,
                maxVisitedStep,
                creationType,
                baseInfo,
                creationMode,
                goalChildren,
                milestones,
                manualTeams,
                draft,
                generatedSignature,
                savedAt: new Date().toISOString(),
            });
            setStorageWarning(!persisted);
        }, 250);
        return () => window.clearTimeout(timer);
    }, [
        activeStep,
        baseInfo,
        creationMode,
        creationType,
        draft,
        generatedSignature,
        goalChildren,
        manualTeams,
        maxVisitedStep,
        milestones,
        proposalOnly,
        externallySeeded,
        storageKey,
        visible,
    ]);

    const allMemberOptions = useMemo(() => {
        const generatedOptions = (draft.memberOptions || []).map((option) => ({
            documentId: option.documentId,
            member: option.member || {fullname: option.name},
        }));
        const combined = [...workspaceMemberships, ...generatedOptions];
        return combined.filter((membership, index) => (
            memberKey(membership)
            && combined.findIndex((item) => memberKey(item) === memberKey(membership)) === index
        ));
    }, [draft.memberOptions, workspaceMemberships]);

    const filteredMembers = useMemo(() => {
        const query = memberQuery.trim().toLocaleLowerCase();
        if (!query) return allMemberOptions;
        return allMemberOptions.filter((membership) => {
            const profile = memberProfile(membership);
            const role = (membership.user_roles || []).find((item) => item.scope_type === "workspace")?.role || "";
            return [
                profile?.fullname,
                profile?.firstname,
                profile?.lastname,
                profile?.jobPosition,
                role,
            ].some((value) => String(value || "").toLocaleLowerCase().includes(query));
        });
    }, [allMemberOptions, memberQuery]);

    const manualOperations = useMemo(() => buildManualOperations({
        creationType,
        baseInfo,
        goalChildren,
        milestones,
        manualTeams,
        creatorUser: workspaceMembershipData,
        allowedTypes,
        allowedEntityTypes,
    }), [
        allowedEntityTypes,
        allowedTypes,
        baseInfo,
        creationType,
        goalChildren,
        manualTeams,
        milestones,
        workspaceMembershipData,
    ]);

    const operations = useMemo(
        () => (proposalOnly || creationMode === "ai" ? (draft.operations || []) : manualOperations),
        [creationMode, draft.operations, manualOperations, proposalOnly],
    );
    const root = rootOperation(operations);
    const previewOperations = operations.length ? operations : manualOperations;
    const previewRootOperation = rootOperation(previewOperations);
    const graph = useMemo(
        () => graphElements(previewOperations, translate),
        [previewOperations, translate],
    );
    const stepItems = useMemo(
        () => STEPS.map(([label, icon]) => ({label: translate(label), icon})),
        [translate],
    );

    const signature = useMemo(
        () => wizardSignature(creationType, baseInfo, goalChildren, milestones, manualTeams),
        [baseInfo, creationType, goalChildren, manualTeams, milestones],
    );
    const generatedStale = creationMode === "ai"
        && Boolean(draft.operations?.length)
        && Boolean(generatedSignature)
        && signature !== generatedSignature;

    const setBaseField = (field, value) => {
        setBaseInfo((current) => ({...current, [field]: value}));
        setErrors((current) => ({...current, [field]: undefined}));
        setError(null);
        setDirty(true);
    };

    const changeType = (type) => {
        if (!allowedTypes.includes(type) || generating || loading || type === creationType) return;
        setCreationType(type);
        setBaseInfo((current) => ({
            ...current,
            rationale: "",
            parentGoal: null,
            relatedGoal: null,
        }));
        setGoalChildren([]);
        setMilestones([]);
        setDraft({
            operations: [],
            memberOptions: draft.memberOptions || [],
            teamOptions: draft.teamOptions || [],
            staffingRecommendations: [],
        });
        setGeneratedSignature("");
        setErrors({});
        setError(null);
        setDirty(true);
    };

    const toggleMember = (membership) => {
        setBaseInfo((current) => {
            const exists = current.members.some((item) => sameRecord(item, membership));
            return {
                ...current,
                members: exists
                    ? current.members.filter((item) => !sameRecord(item, membership))
                    : [...current.members, membership],
            };
        });
        setDirty(true);
    };

    const addGoalChild = () => {
        const availableType = allowedTypes.includes("goal")
            ? "goal"
            : (allowedTypes.includes("project") ? "project" : null);
        if (!availableType) return;
        setGoalChildren((current) => [...current, {
            id: `manual-child-${Date.now()}`,
            type: availableType,
            title: "",
            description: "",
            milestones: [],
        }]);
        setDirty(true);
    };

    const updateGoalChild = (id, field, value) => {
        setGoalChildren((current) => current.map((item) => (
            item.id === id ? {...item, [field]: value} : item
        )));
        setErrors((current) => ({...current, children: undefined}));
        setDirty(true);
    };

    const removeGoalChild = (id) => {
        setGoalChildren((current) => current.filter((item) => item.id !== id));
        setDirty(true);
    };

    const addMilestone = () => {
        if (!allowedEntityTypes.includes("deliverable")) return;
        setMilestones((current) => [...current, {
            id: `manual-milestone-${Date.now()}`,
            title: "",
            description: "",
            dueDate: baseInfo.dueDate,
            tasks: [],
        }]);
        setDirty(true);
    };

    const updateMilestone = (id, field, value) => {
        setMilestones((current) => current.map((item) => (
            item.id === id ? {...item, [field]: value} : item
        )));
        setErrors((current) => ({...current, milestones: undefined}));
        setDirty(true);
    };

    const removeMilestone = (id) => {
        setMilestones((current) => current.filter((item) => item.id !== id));
        setDirty(true);
    };

    const updateChildMilestones = (childId, updater) => {
        setGoalChildren((current) => current.map((child) => (
            child.id === childId
                ? {...child, milestones: updater(child.milestones || [])}
                : child
        )));
        setDirty(true);
    };

    const addChildMilestone = (childId) => updateChildMilestones(childId, (current) => [
        ...current,
        {
            id: `manual-child-milestone-${Date.now()}`,
            title: "",
            description: "",
            dueDate: baseInfo.dueDate,
            tasks: [],
        },
    ]);

    const updateChildMilestone = (childId, milestoneId, field, value) => (
        updateChildMilestones(childId, (current) => current.map((milestone) => (
            milestone.id === milestoneId ? {...milestone, [field]: value} : milestone
        )))
    );

    const removeChildMilestone = (childId, milestoneId) => (
        updateChildMilestones(childId, (current) => current.filter(
            (milestone) => milestone.id !== milestoneId,
        ))
    );

    const updateMilestoneTasks = (milestoneId, updater, childId = null) => {
        const apply = (current) => current.map((milestone) => (
            milestone.id === milestoneId
                ? {...milestone, tasks: updater(milestone.tasks || [])}
                : milestone
        ));
        if (childId) updateChildMilestones(childId, apply);
        else {
            setMilestones(apply);
            setDirty(true);
        }
    };

    const addTask = (milestoneId, childId = null) => updateMilestoneTasks(
        milestoneId,
        (current) => [...current, {
            id: `manual-task-${Date.now()}-${current.length + 1}`,
            title: "",
            description: "",
            dueDate: baseInfo.dueDate,
            assignee: null,
        }],
        childId,
    );

    const updateTask = (milestoneId, taskId, field, value, childId = null) => (
        updateMilestoneTasks(
            milestoneId,
            (current) => current.map((task) => (
                task.id === taskId ? {...task, [field]: value} : task
            )),
            childId,
        )
    );

    const removeTask = (milestoneId, taskId, childId = null) => (
        updateMilestoneTasks(
            milestoneId,
            (current) => current.filter((task) => task.id !== taskId),
            childId,
        )
    );

    const addManualTeam = () => {
        setManualTeams((current) => [...current, {
            id: `manual-team-${Date.now()}`,
            name: "",
            description: "",
            members: [],
        }]);
        setDirty(true);
    };

    const updateManualTeam = (id, field, value) => {
        setManualTeams((current) => current.map((teamItem) => (
            teamItem.id === id ? {...teamItem, [field]: value} : teamItem
        )));
        setDirty(true);
    };

    const removeManualTeam = (id) => {
        setManualTeams((current) => current.filter((teamItem) => teamItem.id !== id));
        setDirty(true);
    };

    const updateOperationPayload = (operationIndex, field, value) => {
        setDraft((current) => ({
            ...current,
            operations: current.operations.map((operation, index) => (
                index === operationIndex
                    ? {...operation, payload: {...operation.payload, [field]: value}}
                    : operation
            )),
        }));
        if (operations[operationIndex] === root) {
            if (field === "title" || field === "description" || field === "dueDate") {
                const nextBaseInfo = {...baseInfo, [field]: value};
                setBaseInfo(nextBaseInfo);
                setGeneratedSignature(wizardSignature(
                    creationType,
                    nextBaseInfo,
                    goalChildren,
                    milestones,
                    manualTeams,
                ));
            }
            if (field === "why" || field === "hypothesis") {
                const nextBaseInfo = {...baseInfo, rationale: value};
                setBaseInfo(nextBaseInfo);
                setGeneratedSignature(wizardSignature(
                    creationType,
                    nextBaseInfo,
                    goalChildren,
                    milestones,
                    manualTeams,
                ));
            }
        }
        setDirty(true);
    };

    const removeOperation = (operationIndex) => {
        const reference = clientReference(draft.operations[operationIndex]);
        setDraft((current) => ({
            ...current,
            operations: current.operations
                .filter((_, index) => index !== operationIndex)
                .map((operation) => ({
                    ...operation,
                    payload: removeReference(operation.payload, reference),
                })),
        }));
        setDirty(true);
    };

    const validateInformation = () => {
        const nextErrors = {};
        const title = baseInfo.title.trim();
        const description = baseInfo.description.trim();
        if (!title) nextErrors.title = "ai.creation.validation.titleRequired";
        else if (title.length > 250) nextErrors.title = "ai.creation.validation.titleLength";
        if (description.length < 10) nextErrors.description = "ai.creation.descriptionRequired";
        else if (description.length > 4000) nextErrors.description = "ai.creation.validation.descriptionLength";
        if (!baseInfo.dueDate) nextErrors.dueDate = "ai.creation.validation.dueDateRequired";
        if (allMemberOptions.length && !baseInfo.owner) {
            nextErrors.owner = "ai.creation.validation.ownerRequired";
        }
        if (
            creationType === "project"
            && baseInfo.startAt
            && baseInfo.dueDate
            && Date.parse(baseInfo.startAt) > Date.parse(baseInfo.dueDate)
        ) {
            nextErrors.startAt = "ai.creation.validation.dateOrder";
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const validateOperations = (items, enforcePersian) => {
        if (!items.length) {
            setError("ai.creation.validation.emptyStructure");
            return false;
        }
        const names = items.map((operation) => operationTitle(operation).trim());
        if (names.some((name) => !name)) {
            setError("ai.creation.validation.itemTitleRequired");
            return false;
        }
        const normalized = names.map((name) => name.toLocaleLowerCase());
        if (new Set(normalized).size !== normalized.length) {
            setError("ai.creation.validation.duplicateTitles");
            return false;
        }
        if (enforcePersian) {
            const invalidPersian = items.some((operation) => [
                operationTitle(operation),
                operation.payload?.description,
                operation.payload?.why,
                operation.payload?.hypothesis,
            ].some((value) => String(value || "").trim() && !PERSIAN_TEXT.test(String(value))));
            if (invalidPersian) {
                setError("ai.creation.validation.persianRequired");
                return false;
            }
        }
        return true;
    };

    const validateMode = () => {
        if (proposalOnly) return validateOperations(operations, false);
        if (creationMode === "ai") {
            if (generatedStale) {
                setError("ai.creation.validation.regenerateRequired");
                return false;
            }
            return validateOperations(operations, true);
        }
        const allMilestones = [
            ...(creationType === "project" ? milestones : []),
            ...goalChildren
                .filter((child) => child.type === "project")
                .flatMap((child) => child.milestones || []),
        ];
        if (creationType === "goal" && goalChildren.some((child) => !child.title.trim())) {
            setErrors((current) => ({...current, children: "ai.creation.validation.childTitleRequired"}));
            return false;
        }
        if (allMilestones.some((milestone) => (
            !milestone.title.trim() || !milestone.dueDate
        ))) {
            setErrors((current) => ({...current, milestones: "ai.creation.validation.milestoneRequired"}));
            return false;
        }
        if (allMilestones.some((milestone) => (
            (milestone.tasks || []).some((task) => !task.title.trim() || !task.dueDate)
        ))) {
            setErrors((current) => ({...current, tasks: "ai.creation.validation.taskRequired"}));
            return false;
        }
        if (manualTeams.some((teamItem) => !teamItem.name.trim())) {
            setErrors((current) => ({...current, teams: "ai.creation.validation.teamRequired"}));
            return false;
        }
        return validateOperations(manualOperations, false);
    };

    const validateStep = (index) => {
        if (workspaceStatus !== "active" || !allowedTypes.length) {
            setError("ai.creation.validation.workspaceUnavailable");
            return false;
        }
        if (proposalOnly) return validateMode();
        if (index === 0) return allowedTypes.includes(creationType);
        if (index === 1) return validateInformation();
        if (index === 2) return true;
        return validateMode();
    };

    const goToStep = (nextStep) => {
        if (nextStep === activeStep || proposalOnly) return;
        if (nextStep < activeStep) {
            setActiveStep(nextStep);
            return;
        }
        if (nextStep > maxVisitedStep + 1) return;
        for (let index = activeStep; index < nextStep; index += 1) {
            if (!validateStep(index)) return;
        }
        setActiveStep(nextStep);
        setMaxVisitedStep((current) => Math.max(current, nextStep));
        setError(null);
    };

    const generate = async () => {
        if (!validateInformation()) {
            setActiveStep(1);
            return false;
        }
        setGenerating(true);
        setError(null);
        try {
            const description = [
                baseInfo.title.trim(),
                baseInfo.description.trim(),
                baseInfo.rationale.trim(),
                JSON.stringify(manualOperations
                    .filter((operation) => (
                        clientReference(operation) !== `root-${creationType}`
                        && operationTitle(operation).trim()
                    ))
                    .map((operation) => ({
                        entityType: operation.entityType,
                        title: operationTitle(operation),
                        description: operation.payload?.description || "",
                    }))),
            ].filter(Boolean).join("\n");
            const generated = await onGenerate?.({creationType, description});
            if (!generated?.operations?.length) throw new Error("ai.error.invalid_creation_draft");
            const supportedOperations = pruneUnsupportedOperations(
                generated.operations,
                creationType,
                allowedTypes,
                allowedEntityTypes,
            );
            const baseMergedOperations = mergeBaseIntoOperations(
                supportedOperations,
                creationType,
                baseInfo,
            );
            const mergedOperations = mergeManualOperations(
                baseMergedOperations,
                manualOperations,
                creationType,
            ).map((operation) => (
                operation.entityType === "task"
                    ? {
                        ...operation,
                        payload: {
                            ...operation.payload,
                            creatorUser: relationFor(workspaceMembershipData),
                            extra: {type: "ai-generated"},
                        },
                    }
                    : operation
            ));
            if (!rootOperation(mergedOperations)) throw new Error("ai.error.invalid_creation_draft");
            setDraft({
                ...clone(generated),
                operations: mergedOperations,
                memberOptions: clone(generated.memberOptions || []),
                teamOptions: clone(generated.teamOptions || []),
                staffingRecommendations: clone(generated.staffingRecommendations || []),
            });
            setGeneratedSignature(signature);
            setDirty(true);
            return true;
        } catch (requestError) {
            setError(requestError?.code || requestError?.message || "ai.error.generic");
            return false;
        } finally {
            setGenerating(false);
        }
    };

    const finish = async () => {
        if (!validateMode()) return;
        const preparedOperations = operations.map((operation) => ({
            ...operation,
            payload: Object.fromEntries(
                Object.entries(operation.payload || {}).map(([field, value]) => (
                    ["dueDate", "startAt", "doneAt"].includes(field)
                        ? [field, normalizeDate(value)]
                        : [field, value]
                )),
            ),
        }));
        try {
            await onSubmit?.({
                title: operationTitle(root || preparedOperations[0]) || draft.title,
                contextHash: draft.contextHash,
                operations: preparedOperations,
            });
            if (!proposalOnly && !externallySeeded) removeLocalDraft(storageKey);
        } catch {
            // The caller owns the user-facing error; retain the draft for retry.
        }
    };

    const next = () => {
        if (activeStep < STEPS.length - 1) goToStep(activeStep + 1);
    };

    const requestClose = () => {
        if (loading || generating) return;
        if (!dirty) {
            onHide?.();
            return;
        }
        confirmDialog({
            group: closeDialogGroup,
            header: translate("ai.creation.close.title"),
            message: translate("ai.creation.close.message"),
            icon: "pi pi-exclamation-triangle",
            acceptLabel: translate("ai.creation.close.accept"),
            rejectLabel: translate("cancel"),
            acceptClassName: "p-button-danger text-xs gap-2",
            rejectClassName: "p-button-text text-xs gap-2",
            accept: onHide,
        });
    };

    const clearDraft = () => {
        if (loading || generating || proposalOnly || externallySeeded) return;
        confirmDialog({
            group: clearDialogGroup,
            header: translate("ai.creation.clear.title"),
            message: translate("ai.creation.clear.message"),
            icon: "pi pi-trash",
            acceptLabel: translate("ai.creation.clear.accept"),
            rejectLabel: translate("cancel"),
            acceptClassName: "p-button-danger text-xs gap-2",
            rejectClassName: "p-button-text text-xs gap-2",
            accept: () => {
                hydrated.current = false;
                removeLocalDraft(storageKey);
                setActiveStep(0);
                setMaxVisitedStep(0);
                setCreationType(allowedTypes.includes(initialType) ? initialType : allowedTypes[0]);
                setBaseInfo(defaultBaseInfo(workspaceMembershipData));
                setCreationMode("ai");
                setGoalChildren([]);
                setMilestones([]);
                setManualTeams([]);
                setDraft({
                    operations: [],
                    memberOptions: [],
                    teamOptions: [],
                    staffingRecommendations: [],
                });
                setGeneratedSignature("");
                setMemberQuery("");
                setErrors({});
                setError(null);
                setStorageWarning(false);
                setDirty(false);
                window.setTimeout(() => {
                    hydrated.current = true;
                }, 0);
            },
        });
    };

    const selectItemTemplate = (option) => (
        <div className="flex align-items-center gap-2 text-xs">
            <span>{option?.title || option?.name}</span>
        </div>
    );

    const selectValueTemplate = (option, props) => (
        option
            ? selectItemTemplate(option)
            : <span className="text-xs text-500">{props?.placeholder}</span>
    );

    const memberItemTemplate = (option) => (
        <div className="flex align-items-center rtl gap-2 text-xs">
            <Avatar memberData={memberProfile(option)}/>
            <div className="flex flex-column gap-1">
                <span>{memberProfile(option)?.fullname}</span>
                {memberProfile(option)?.jobPosition && (
                    <span className="text-xs text-500">{memberProfile(option).jobPosition}</span>
                )}
            </div>
        </div>
    );

    const memberValueTemplate = (option, props) => (
        option
            ? memberItemTemplate(option)
            : <span className="text-xs text-500">{props?.placeholder}</span>
    );

    const memberMultiSelectItemTemplate = (option) => (
        <div className="w-fit flex align-items-center gap-1 text-xs white-space-nowrap">
            {memberProfile(option)?.fullname || memberKey(option)}
        </div>
    );

    const memberMultiSelectSelectedItemTemplate = (option) => (
        option
            ? (
                <div className="w-fit flex-none flex align-items-center gap-1 text-xs p-1 px-2 surface-100 border-round-2xl white-space-nowrap">
                    {memberProfile(option)?.fullname || memberKey(option)}
                </div>
            )
            : (
                <span className="flex align-items-center align-self-stretch text-xs text-500 white-space-nowrap">
                    {translate("ai.creation.manual.teamMembers")}
                </span>
            )
    );

    const renderFieldError = (field) => (
        errors[field]
            ? <small id={`ai-creation-${field}-error`} className="p-error text-xs">{translate(errors[field])}</small>
            : null
    );

    const renderDateField = ({
        label,
        value,
        field,
        onChange = (nextValue) => setBaseField(field, nextValue),
    }) => (
        <label className="flex flex-column gap-2 text-xs">
            <span className="font-semibold">{label}</span>
            <PrimeDatePicker
                inputClassName={`w-full text-xs ${errors[field] ? "p-invalid" : ""}`}
                value={unixDate(value)}
                placeholder={label}
                onChange={(event) => onChange(
                    event.value ? new Date(event.value * 1000).toISOString() : null,
                )}
            />
            {renderFieldError(field)}
        </label>
    );

    const renderOperationEditor = (operation, operationIndex) => {
        const field = titleField(operation.entityType);
        const dateField = operation.entityType === "deliverable" ? "doneAt" : "dueDate";
        const canRemove = operation !== root;
        return (
            <div
                className="ai-action-wizard-result-item flex flex-column gap-3"
                key={clientReference(operation) || operationIndex}
            >
                <div className="flex align-items-center justify-content-between gap-2">
                    <div className="flex align-items-center gap-2">
                        <i className={`${ENTITY_ICONS[operation.entityType] || "pi pi-file"} text-primary`}/>
                        <span className="font-semibold text-xs">
                            {translate(`ai.action.entity.${operation.entityType}`)}
                        </span>
                    </div>
                    {canRemove && (
                        <Button
                            type="button"
                            text
                            rounded
                            severity="danger"
                            icon="pi pi-trash"
                            aria-label={translate("delete")}
                            onClick={() => removeOperation(operationIndex)}
                        />
                    )}
                </div>
                <label className="flex flex-column gap-2 text-xs">
                    <span>{translate("ai.actionWizard.name")}</span>
                    <InputText
                        className="w-full text-xs"
                        value={operation.payload?.[field] || ""}
                        onChange={(event) => updateOperationPayload(
                            operationIndex,
                            field,
                            event.target.value,
                        )}
                    />
                </label>
                {operation.payload?.description !== undefined && (
                    <label className="flex flex-column gap-2 text-xs">
                        <span>{translate("ai.actionWizard.description")}</span>
                        <InputTextarea
                            autoResize
                            rows={2}
                            className="w-full text-xs line-height-3"
                            value={operation.payload.description || ""}
                            onChange={(event) => updateOperationPayload(
                                operationIndex,
                                "description",
                                event.target.value,
                            )}
                        />
                    </label>
                )}
                {operation.payload?.why !== undefined && (
                    <label className="flex flex-column gap-2 text-xs">
                        <span>{translate("ai.actionWizard.why")}</span>
                        <InputTextarea
                            autoResize
                            rows={2}
                            className="w-full text-xs"
                            value={operation.payload.why || ""}
                            onChange={(event) => updateOperationPayload(
                                operationIndex,
                                "why",
                                event.target.value,
                            )}
                        />
                    </label>
                )}
                {operation.payload?.hypothesis !== undefined && (
                    <label className="flex flex-column gap-2 text-xs">
                        <span>{translate("ai.actionWizard.hypothesis")}</span>
                        <InputTextarea
                            autoResize
                            rows={2}
                            className="w-full text-xs"
                            value={operation.payload.hypothesis || ""}
                            onChange={(event) => updateOperationPayload(
                                operationIndex,
                                "hypothesis",
                                event.target.value,
                            )}
                        />
                    </label>
                )}
                {["goal", "project", "deliverable", "task"].includes(operation.entityType) && (
                    <label className="flex flex-column gap-2 text-xs">
                        <span>{translate("ai.actionWizard.dueDate")}</span>
                        <PrimeDatePicker
                            inputClassName="w-full text-xs"
                            value={unixDate(operation.payload?.[dateField])}
                            placeholder={translate("ai.actionWizard.dueDate")}
                            onChange={(event) => updateOperationPayload(
                                operationIndex,
                                dateField,
                                event.value ? new Date(event.value * 1000).toISOString() : null,
                            )}
                        />
                    </label>
                )}
            </div>
        );
    };

    const renderStepOne = () => (
        <div className="flex flex-column gap-4">
            <div className="flex flex-column gap-1">
                <h2 className="m-0 text-base">{translate("ai.creation.type.title")}</h2>
                <p className="m-0 text-xs text-500 line-height-3">
                    {translate("ai.creation.type.subtitle")}
                </p>
            </div>
            <div className="grid m-0">
                {["goal", "project"].map((type) => (
                    <div className="col-12 p-1" key={type}>
                        <TypeCard
                            type={type}
                            selected={creationType === type}
                            disabled={!allowedTypes.includes(type)}
                            translate={translate}
                            onClick={() => changeType(type)}
                        />
                    </div>
                ))}
            </div>
            {!allowedTypes.length && (
                <Message severity="warn" text={translate("ai.creation.validation.noPermission")}/>
            )}
        </div>
    );

    const renderStepTwo = () => (
        <div className="flex flex-column gap-4">
            <div className="flex flex-column gap-1">
                <h2 className="m-0 text-base">{translate(`ai.creation.information.${creationType}.title`)}</h2>
                <p className="m-0 text-xs text-500 line-height-3">
                    {translate("ai.creation.information.subtitle")}
                </p>
            </div>
            <label className="flex flex-column gap-2 text-xs">
                <span className="font-semibold">{translate("ai.actionWizard.name")}</span>
                <InputText
                    id="ai-creation-title"
                    className={`w-full text-xs ${errors.title ? "p-invalid" : ""}`}
                    value={baseInfo.title}
                    maxLength={250}
                    aria-describedby={errors.title ? "ai-creation-title-error" : undefined}
                    placeholder={translate(`ai.creation.titlePlaceholder.${creationType}`)}
                    onChange={(event) => setBaseField("title", event.target.value)}
                />
                {renderFieldError("title")}
            </label>
            <label className="flex flex-column gap-2 text-xs">
                <span className="font-semibold">{translate("ai.actionWizard.description")}</span>
                <InputTextarea
                    id="ai-creation-description"
                    autoResize
                    rows={5}
                    className={`w-full text-xs line-height-3 ${errors.description ? "p-invalid" : ""}`}
                    value={baseInfo.description}
                    maxLength={4000}
                    aria-describedby={errors.description ? "ai-creation-description-error" : undefined}
                    placeholder={translate(`ai.creation.descriptionPlaceholder.${creationType}`)}
                    onChange={(event) => setBaseField("description", event.target.value)}
                />
                <small className="text-xs text-500">{translate("ai.creation.descriptionHint")}</small>
                {renderFieldError("description")}
            </label>
            <label className="flex flex-column gap-2 text-xs">
                <span className="font-semibold">
                    {translate(creationType === "goal" ? "ai.actionWizard.why" : "ai.actionWizard.hypothesis")}
                </span>
                <InputTextarea
                    autoResize
                    rows={3}
                    className="w-full text-xs line-height-3"
                    value={baseInfo.rationale}
                    placeholder={translate(`ai.creation.rationalePlaceholder.${creationType}`)}
                    onChange={(event) => setBaseField("rationale", event.target.value)}
                />
            </label>
            {creationType === "project" && renderDateField({
                label: translate("ai.creation.startDate"),
                value: baseInfo.startAt,
                field: "startAt",
            })}
            {renderDateField({
                label: translate("ai.actionWizard.dueDate"),
                value: baseInfo.dueDate,
                field: "dueDate",
            })}
            <label className="flex flex-column gap-2 text-xs">
                <span className="font-semibold">{translate("ai.actionWizard.owner")}</span>
                <ClearableDropdown
                    filter
                    filterBy="member.fullname,member.firstname,member.lastname,member.jobPosition"
                    className={`w-full text-xs ${errors.owner ? "p-invalid" : ""}`}
                    itemTemplate={memberItemTemplate}
                    valueTemplate={memberValueTemplate}
                    value={baseInfo.owner}
                    options={allMemberOptions}
                    placeholder={translate("ai.actionWizard.selectMember")}
                    onChange={(event) => setBaseField("owner", event.value)}
                />
                {renderFieldError("owner")}
            </label>
            {creationType === "goal" && (
                <>
                    <label className="flex flex-column gap-2 text-xs">
                        <span className="font-semibold">{translate("cycle")}</span>
                        <CyclePicker
                            value={baseInfo.cycle}
                            translate={translate}
                            onChange={(value) => setBaseField("cycle", value)}
                        />
                    </label>
                    <label className="flex flex-column gap-2 text-xs">
                        <span className="font-semibold">{translate("ai.creation.parentGoal")}</span>
                        <ClearableDropdown
                            filter
                            filterBy="title"
                            className="w-full text-xs"
                            itemTemplate={selectItemTemplate}
                            valueTemplate={selectValueTemplate}
                            value={baseInfo.parentGoal}
                            options={goals}
                            placeholder={translate("ai.creation.parentGoalPlaceholder")}
                            onChange={(event) => setBaseField("parentGoal", event.value)}
                        />
                    </label>
                </>
            )}
            {creationType === "project" && (
                <label className="flex flex-column gap-2 text-xs">
                    <span className="font-semibold">{translate("ai.creation.relatedGoal")}</span>
                    <ClearableDropdown
                        filter
                        filterBy="title"
                        className="w-full text-xs"
                        itemTemplate={selectItemTemplate}
                        valueTemplate={selectValueTemplate}
                        value={baseInfo.relatedGoal}
                        options={goals}
                        placeholder={translate("ai.creation.relatedGoalPlaceholder")}
                        onChange={(event) => setBaseField("relatedGoal", event.value)}
                    />
                </label>
            )}
            <label className="flex flex-column gap-2 text-xs">
                <span className="font-semibold">{translate("ai.actionWizard.team")}</span>
                <ClearableDropdown
                    filter
                    filterBy="name"
                    className="w-full text-xs"
                    itemTemplate={selectItemTemplate}
                    valueTemplate={selectValueTemplate}
                    value={baseInfo.team}
                    options={teams}
                    placeholder={translate("ai.actionWizard.selectTeam")}
                    onChange={(event) => setBaseField("team", event.value)}
                />
            </label>
        </div>
    );

    const renderStepThree = () => (
        <div className="flex flex-column gap-4">
            <div className="flex flex-column gap-1">
                <h2 className="m-0 text-base">{translate("ai.creation.members.title")}</h2>
                <p className="m-0 text-xs text-500 line-height-3">
                    {translate(`ai.creation.members.${creationType}.subtitle`)}
                </p>
            </div>
            <IconField className="w-full">
                <InputIcon className="pi pi-search"/>
                <InputText
                    className="w-full text-xs"
                    value={memberQuery}
                    placeholder={translate("search-members-placeholder")}
                    aria-label={translate("search-members-placeholder")}
                    onChange={(event) => setMemberQuery(event.target.value)}
                />
            </IconField>
            <div className="ai-action-wizard-member-list flex flex-column scrollbar-hide">
                {filteredMembers.map((membership) => {
                    const key = memberKey(membership);
                    const checked = baseInfo.members.some((item) => sameRecord(item, membership));
                    const profile = memberProfile(membership);
                    const role = (membership.user_roles || []).find((item) => item.scope_type === "workspace")?.role;
                    return (
                        <label
                            htmlFor={`ai-creation-member-${key}`}
                            className={`ai-action-wizard-member-row ${checked ? "is-selected" : ""}`}
                            key={key}
                        >
                            <div className="flex align-items-center gap-2 min-w-0">
                                <Avatar memberData={profile}/>
                                <div className="flex flex-column gap-1 min-w-0">
                                    <span className="text-xs font-semibold overflow-hidden text-overflow-ellipsis">
                                        {profile?.fullname || key}
                                    </span>
                                    <span className="text-xs text-500 overflow-hidden text-overflow-ellipsis">
                                        {profile?.jobPosition || (role ? translate(role) : translate("workspace-member"))}
                                    </span>
                                </div>
                            </div>
                            <Checkbox
                                inputId={`ai-creation-member-${key}`}
                                checked={checked}
                                onChange={() => toggleMember(membership)}
                            />
                        </label>
                    );
                })}
                {!filteredMembers.length && (
                    <div className="p-4 text-center text-xs text-500">
                        {translate("ai.creation.members.empty")}
                    </div>
                )}
            </div>
            {!!baseInfo.members.length && (
                <div className="flex flex-column gap-2">
                    <span className="text-xs font-semibold">
                        {translate("ai.creation.members.selected")}
                    </span>
                    <div className="flex flex-wrap gap-2">
                        {baseInfo.members.map((membership) => (
                            <Tag
                                key={memberKey(membership)}
                                className="text-xs"
                                icon="pi pi-user"
                                value={memberProfile(membership)?.fullname || memberKey(membership)}
                            />
                        ))}
                    </div>
                </div>
            )}
            <div className="surface-50 border-round-lg p-3 flex align-items-start gap-2">
                <i className="pi pi-info-circle text-primary mt-1"/>
                <span className="text-xs text-600 line-height-3">
                    {translate("ai.creation.members.staffingHint")}
                </span>
            </div>
        </div>
    );

    const renderModeCards = () => (
        <div className="grid m-0">
            {[
                ["ai", "pi pi-sparkles"],
                ["manual", "pi pi-pencil"],
            ].map(([mode, icon]) => (
                <div className="col-12 p-1" key={mode}>
                    <Button
                        type="button"
                        outlined={creationMode !== mode}
                        aria-pressed={creationMode === mode}
                        className={`ai-action-wizard-mode-card text-xs ${creationMode === mode ? "is-selected" : ""}`}
                        onClick={() => {
                            setCreationMode(mode);
                            setError(null);
                            setDirty(true);
                        }}
                    >
                        <span className={`ai-action-wizard-mode-icon ${creationMode === mode ? "bg-primary text-primary-contrast" : "surface-100 text-500"}`}>
                            <i className={`${icon} text-lg`}/>
                        </span>
                        <span className="flex flex-column gap-1 text-right">
                            <span className="font-semibold text-xs">
                                {translate(`ai.creation.mode.${mode}`)}
                            </span>
                            <span className="font-normal text-xs text-500 line-height-3">
                                {translate(`ai.creation.mode.${mode}.description`)}
                            </span>
                        </span>
                    </Button>
                </div>
            ))}
        </div>
    );

    const renderMilestoneGrid = ({
        items,
        childId = null,
        onAdd,
        onUpdate,
        onRemove,
    }) => (
        <div className="flex flex-column gap-3">
            <div className="flex align-items-center justify-content-between gap-2">
                <div className="flex flex-column gap-1">
                    <div className="flex gap-2 align-items-center">
                        <span className="font-semibold text-xs">{translate("ai.creation.manual.milestones")}</span>
                        <Button
                            type="button"
                            outlined
                            className="text-2xs gap-2 p-1 px-2"
                            icon="pi pi-plus text-xs"
                            label={translate("ai.creation.manual.addMilestone")}
                            disabled={!allowedEntityTypes.includes("deliverable")}
                            onClick={onAdd}
                        />
                    </div>

                    <span className="text-xs text-500">{translate("ai.creation.manual.milestonesHint")}</span>
                </div>

            </div>
            <div className="grid m-0">
                {items.map((milestone) => (
                    <div className="col-12 p-1" key={milestone.id}>
                        <div className="ai-action-wizard-manual-row flex flex-column gap-3 h-full">
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-box text-primary"/>
                                <InputText
                                    className="flex-1 min-w-0 text-xs"
                                    value={milestone.title}
                                    maxLength={250}
                                    placeholder={translate("ai.creation.manual.milestoneTitle")}
                                    onChange={(event) => onUpdate(
                                        milestone.id,
                                        "title",
                                        event.target.value,
                                    )}
                                />
                                <Button
                                    type="button"
                                    text
                                    rounded
                                    severity="danger"
                                    icon="pi pi-trash"
                                    aria-label={translate("delete")}
                                    onClick={() => onRemove(milestone.id)}
                                />
                            </div>
                            <InputTextarea
                                autoResize
                                rows={2}
                                className="w-full text-xs"
                                value={milestone.description}
                                placeholder={translate("ai.creation.manual.itemDescription")}
                                onChange={(event) => onUpdate(
                                    milestone.id,
                                    "description",
                                    event.target.value,
                                )}
                            />
                            <PrimeDatePicker
                                inputClassName="w-full text-xs"
                                value={unixDate(milestone.dueDate)}
                                placeholder={translate("ai.actionWizard.dueDate")}
                                onChange={(event) => onUpdate(
                                    milestone.id,
                                    "dueDate",
                                    event.value ? new Date(event.value * 1000).toISOString() : null,
                                )}
                            />
                            <div className="flex align-items-center justify-content-between gap-2 pt-2 border-top-1 border-200">
                                <span className="font-semibold text-xs">
                                    {translate("ai.creation.manual.tasks")}
                                </span>
                                <Button
                                    type="button"
                                    text
                                    className="text-2xs gap-2 p-1 px-2"
                                    icon="pi pi-plus text-xs"
                                    label={translate("ai.creation.manual.addTask")}
                                    disabled={!allowedEntityTypes.includes("task")}
                                    onClick={() => addTask(milestone.id, childId)}
                                />
                            </div>
                            {(milestone.tasks || []).map((task) => (
                                <div className="surface-50 border-round p-2 flex flex-column gap-2" key={task.id}>
                                    <div className="flex align-items-center gap-2">
                                        <InputText
                                            className="flex-1 min-w-0 text-xs"
                                            value={task.title}
                                            placeholder={translate("ai.creation.manual.taskTitle")}
                                            onChange={(event) => updateTask(
                                                milestone.id,
                                                task.id,
                                                "title",
                                                event.target.value,
                                                childId,
                                            )}
                                        />
                                        <Button
                                            type="button"
                                            text
                                            rounded
                                            severity="danger"
                                            icon="pi pi-trash"
                                            aria-label={translate("delete")}
                                            onClick={() => removeTask(milestone.id, task.id, childId)}
                                        />
                                    </div>
                                    <InputTextarea
                                        autoResize
                                        rows={2}
                                        className="w-full text-xs"
                                        value={task.description}
                                        placeholder={translate("ai.creation.manual.itemDescription")}
                                        onChange={(event) => updateTask(
                                            milestone.id,
                                            task.id,
                                            "description",
                                            event.target.value,
                                            childId,
                                        )}
                                    />
                                    <PrimeDatePicker
                                        inputClassName="w-full text-xs"
                                        value={unixDate(task.dueDate)}
                                        placeholder={translate("ai.actionWizard.dueDate")}
                                        onChange={(event) => updateTask(
                                            milestone.id,
                                            task.id,
                                            "dueDate",
                                            event.value ? new Date(event.value * 1000).toISOString() : null,
                                            childId,
                                        )}
                                    />
                                    <ClearableDropdown
                                        filter
                                        filterBy="member.fullname,member.firstname,member.lastname"
                                        className="w-full text-xs"
                                        value={task.assignee}
                                        options={allMemberOptions}
                                        itemTemplate={memberItemTemplate}
                                        valueTemplate={memberValueTemplate}
                                        placeholder={translate("ai.actionWizard.selectMember")}
                                        onChange={(event) => updateTask(
                                            milestone.id,
                                            task.id,
                                            "assignee",
                                            event.value,
                                            childId,
                                        )}
                                    />
                                </div>
                            ))}
                            {!(milestone.tasks || []).length && (
                                <span className="text-xs text-500">
                                    {translate("ai.creation.manual.noTasks")}
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            {!items.length && (
                <div className="surface-50 border-round-lg p-3 text-xs text-500 line-height-3">
                    {translate("ai.creation.manual.noMilestones")}
                </div>
            )}
            {!allowedEntityTypes.includes("deliverable") && (
                <Message severity="warn" text={translate("ai.creation.validation.milestonePermission")}/>
            )}
            {renderFieldError("milestones")}
            {renderFieldError("tasks")}
        </div>
    );

    const renderManualTeams = () => (
        <div className="flex flex-column gap-3 pt-3 border-top-1 border-200">
            <div className="flex align-items-center justify-content-between gap-2">
                <div className="flex flex-column gap-1">
                    <div className="flex gap-2 align-items-center">
                        <span className="font-semibold text-xs">{translate("ai.creation.manual.teams")}</span>
                        <Button
                            type="button"
                            pt={{icon:()=>({className:"mr-0"})}}
                            outlined
                            className="text-2xs gap-2 p-1 px-2"
                            icon="pi pi-plus text-xs"
                            label={translate("ai.creation.manual.addTeam")}
                            disabled={!allowedEntityTypes.includes("team")}
                            onClick={addManualTeam}
                        />
                    </div>

                    <span className="text-xs text-500">{translate("ai.creation.manual.teamsHint")}</span>
                </div>

            </div>
            {manualTeams.map((teamItem) => (
                <div className="ai-action-wizard-manual-row flex flex-column gap-2" key={teamItem.id}>
                    <div className="flex align-items-center gap-2">
                        <InputText
                            className="flex-1 min-w-0 text-xs"
                            value={teamItem.name}
                            placeholder={translate("ai.creation.manual.teamName")}
                            onChange={(event) => updateManualTeam(teamItem.id, "name", event.target.value)}
                        />
                        <Button
                            type="button"
                            text
                            rounded
                            severity="danger"
                            icon="pi pi-trash"
                            aria-label={translate("delete")}
                            onClick={() => removeManualTeam(teamItem.id)}
                        />
                    </div>
                    <InputTextarea
                        autoResize
                        rows={2}
                        className="w-full text-xs"
                        value={teamItem.description}
                        placeholder={translate("ai.creation.manual.itemDescription")}
                        onChange={(event) => updateManualTeam(teamItem.id, "description", event.target.value)}
                    />
                    <div className="flex align-items-center gap-2">
                        <div className="flex-1 min-w-0 border-round border-1 border-300 flex justify-content-between pr-2">
                            <MultiSelect
                                filter
                                filterBy="member.fullname"
                                className="w-full text-xs"
                                panelClassName="rtl"
                                pt={MULTISELECT_PT}
                                value={teamItem.members}
                                options={allMemberOptions}
                                maxSelectedLabels={null}
                                itemTemplate={memberMultiSelectItemTemplate}
                                selectedItemTemplate={memberMultiSelectSelectedItemTemplate}
                                filterTemplate={(options) => (
                                    <InputText
                                        placeholder={translate("search-members-placeholder")}
                                        onChange={options.filterOptions.filter}
                                        className="p-2 w-full text-xs"
                                    />
                                )}
                                placeholder={translate("ai.creation.manual.teamMembers")}
                                onChange={(event) => updateManualTeam(teamItem.id, "members", event.value)}
                            />
                        </div>
                        <Button
                            type="button"
                            rounded
                            text
                            severity="danger"
                            icon="pi pi-trash"
                            aria-label={translate("clear")}
                            disabled={!teamItem.members?.length}
                            onClick={() => updateManualTeam(teamItem.id, "members", [])}
                        />
                    </div>
                </div>
            ))}
            {!manualTeams.length && (
                <span className="text-xs text-500">{translate("ai.creation.manual.noTeams")}</span>
            )}
            {renderFieldError("teams")}
        </div>
    );

    const renderManualGoal = () => (
        <div className="flex flex-column gap-4">
            <div className="flex align-items-center justify-content-between gap-2">
                <div className="flex flex-column gap-1">
                    <span className="font-semibold text-xs">{translate("ai.creation.manual.goalChildren")}</span>
                    <span className="text-xs text-500">{translate("ai.creation.manual.goalChildrenHint")}</span>
                </div>
                <Button
                    type="button"
                    outlined
                    className="text-xs gap-2 flex-none"
                    icon="pi pi-plus"
                    label={translate("ai.creation.manual.addChild")}
                    disabled={!allowedTypes.length}
                    onClick={addGoalChild}
                />
            </div>
            {goalChildren.map((child) => (
                <div className="ai-action-wizard-manual-row flex flex-column gap-3" key={child.id}>
                    <div className="flex align-items-center gap-2">
                        <Dropdown
                            className="flex-1 text-xs"
                            panelClassName="rtl text-xs"
                            pt={DROPDOWN_PT}
                            value={child.type}
                            options={allowedTypes.map((type) => ({
                                label: translate(`ai.action.entity.${type}`),
                                value: type,
                            }))}
                            onChange={(event) => updateGoalChild(child.id, "type", event.value)}
                        />
                        <Button
                            type="button"
                            text
                            rounded
                            severity="danger"
                            icon="pi pi-trash"
                            aria-label={translate("delete")}
                            onClick={() => removeGoalChild(child.id)}
                        />
                    </div>
                    <InputText
                        className="w-full text-xs"
                        value={child.title}
                        maxLength={250}
                        placeholder={translate(`ai.creation.manual.${child.type}Title`)}
                        onChange={(event) => updateGoalChild(child.id, "title", event.target.value)}
                    />
                    <InputTextarea
                        autoResize
                        rows={2}
                        className="w-full text-xs"
                        value={child.description}
                        placeholder={translate("ai.creation.manual.itemDescription")}
                        onChange={(event) => updateGoalChild(child.id, "description", event.target.value)}
                    />
                    {child.type === "project" && renderMilestoneGrid({
                        items: child.milestones || [],
                        childId: child.id,
                        onAdd: () => addChildMilestone(child.id),
                        onUpdate: (milestoneId, field, value) => (
                            updateChildMilestone(child.id, milestoneId, field, value)
                        ),
                        onRemove: (milestoneId) => removeChildMilestone(child.id, milestoneId),
                    })}
                </div>
            ))}
            {!goalChildren.length && (
                <div className="surface-50 border-round-lg p-3 text-xs text-500 line-height-3">
                    {translate("ai.creation.manual.noGoalChildren")}
                </div>
            )}
            {renderFieldError("children")}
            {renderManualTeams()}
        </div>
    );

    const renderManualProject = () => (
        <div className="flex flex-column gap-4">
            {renderMilestoneGrid({
                items: milestones,
                onAdd: addMilestone,
                onUpdate: updateMilestone,
                onRemove: removeMilestone,
            })}
            {renderManualTeams()}
        </div>
    );

    const renderStaffingRecommendations = () => (
        draft.staffingRecommendations?.length ? (
            <div className="flex flex-column gap-3 pt-3 border-top-1 border-200">
                <span className="font-semibold text-xs">{translate("ai.creation.staffingRecommendations")}</span>
                {draft.staffingRecommendations.map((recommendation) => (
                    <div className="ai-action-wizard-result-item flex flex-column gap-2" key={recommendation.key}>
                        <div className="flex align-items-center justify-content-between gap-2">
                            <div className="flex align-items-center gap-2 min-w-0">
                                <i className={`${recommendation.type === "outsource" ? "pi pi-external-link" : "pi pi-user-plus"} text-primary`}/>
                                <span className="font-semibold text-xs">{recommendation.title}</span>
                            </div>
                            <Tag
                                className="text-xs flex-none"
                                severity={recommendation.type === "outsource" ? "info" : "success"}
                                value={translate(`ai.creation.staffingType.${recommendation.type}`)}
                            />
                        </div>
                        <span className="text-xs text-600 line-height-3">{recommendation.description}</span>
                        {recommendation.reason && (
                            <span className="text-xs text-500 line-height-3">{recommendation.reason}</span>
                        )}
                        {!!recommendation.requiredSkills?.length && (
                            <div className="flex flex-wrap gap-2">
                                {recommendation.requiredSkills.map((skill) => (
                                    <Tag className="text-xs" key={skill} value={skill}/>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        ) : null
    );

    const renderStepFour = () => (
        <div className="flex flex-column gap-4">
            <div className="flex flex-column gap-1">
                <h2 className="m-0 text-base">
                    {translate(proposalOnly ? "ai.creation.proposalReview" : "ai.creation.mode.title")}
                </h2>
                <p className="m-0 text-xs text-500 line-height-3">
                    {translate(proposalOnly ? "ai.creation.proposalReviewHint" : "ai.creation.mode.subtitle")}
                </p>
            </div>
            {!proposalOnly && renderModeCards()}
            {(proposalOnly || creationMode === "ai") && (
                <div className="flex flex-column gap-3">
                    {generating && (
                        <div className="ai-action-wizard-generating flex flex-column align-items-center justify-content-center gap-3 text-center">
                            <ProgressSpinner className="w-3rem h-3rem"/>
                            <span className="font-semibold text-xs">{translate("ai.creation.generating")}</span>
                            <span className="text-xs text-500 line-height-3">{translate("ai.creation.generatingHint")}</span>
                        </div>
                    )}
                    {!generating && !operations.length && (
                        <div className="surface-50 border-round-lg p-4 flex flex-column align-items-center gap-3 text-center">
                            <i className="pi pi-sparkles text-2xl text-primary"/>
                            <span className="text-xs text-600 line-height-3">
                                {translate("ai.creation.aiReady")}
                            </span>
                        </div>
                    )}
                    {!generating && !!operations.length && (
                        <>
                            <div className="flex align-items-center justify-content-between gap-2">
                                <div className="flex align-items-center gap-2">
                                    <i className="pi pi-check-circle text-green-500"/>
                                    <span className="font-semibold text-xs">
                                        {translate("ai.creation.generatedStructure")}
                                    </span>
                                </div>
                                {!proposalOnly && (
                                    <Button
                                        type="button"
                                        text
                                        className="text-xs gap-2"
                                        icon="pi pi-refresh"
                                        label={translate("ai.creation.regenerate")}
                                        onClick={generate}
                                    />
                                )}
                            </div>
                            {generatedStale && (
                                <Message severity="warn" text={translate("ai.creation.validation.regenerateRequired")}/>
                            )}
                            {operations.map(renderOperationEditor)}
                            {renderStaffingRecommendations()}
                        </>
                    )}
                </div>
            )}
            {!proposalOnly && creationMode === "manual" && (
                creationType === "goal" ? renderManualGoal() : renderManualProject()
            )}
            {error && <Message severity="error" text={translate(error)}/>}
        </div>
    );

    const previewRoot = previewRootOperation;
    const previewPayload = previewRoot?.payload || {};
    const previewTitle = operationTitle(previewRoot) || baseInfo.title || translate("ai.actionWizard.untitled");
    const previewDescription = previewPayload.description || baseInfo.description || translate("ai.creation.previewDescriptionEmpty");
    const previewDueDate = previewPayload.dueDate || previewPayload.doneAt || baseInfo.dueDate;
    const previewType = previewRoot?.entityType || creationType;
    const previewMembers = useMemo(() => {
        const ids = new Set([
            ...((previewPayload.members || []).map(relationDocumentId)),
            ...(baseInfo.members || []).map(memberKey),
            memberKey(baseInfo.owner),
        ].filter(Boolean));
        return allMemberOptions.filter((membership) => ids.has(memberKey(membership)));
    }, [allMemberOptions, baseInfo.members, baseInfo.owner, previewPayload.members]);
    const previewMuted = activeStep < 3 && !(creationMode === "ai" && operations.length);

    const renderPreview = () => (
        <div className="ai-action-wizard-preview-document">
            <div className="ai-action-wizard-preview-meta">
                <div className="flex flex-wrap align-items-center gap-2">
                    <Tag
                        className="text-xs"
                        icon={ENTITY_ICONS[previewType]}
                        value={translate(`ai.action.entity.${previewType}`)}
                    />
                    <Tag
                        className="text-xs"
                        severity={creationMode === "ai" ? "info" : "secondary"}
                        icon={creationMode === "ai" ? "pi pi-sparkles" : "pi pi-pencil"}
                        value={translate(`ai.creation.mode.${creationMode}`)}
                    />
                </div>
                <div className="flex align-items-center gap-2 text-xs text-500">
                    <i className="pi pi-calendar"/>
                    <span>{displayDate(previewDueDate)}</span>
                </div>
            </div>
            <div className="ai-action-wizard-preview-heading">
                <span className="text-xs text-primary font-semibold">
                    {translate("ai.actionWizard.livePreview")}
                </span>
                <h1 className="m-0">{previewTitle}</h1>
                <p className="m-0 text-600 line-height-3">{previewDescription}</p>
            </div>
            {!!previewMembers.length && (
                <div className="flex align-items-center gap-3">
                    <span className="text-xs text-500">{translate("ai.creation.preview.team")}</span>
                    <div className="ai-action-wizard-avatar-stack flex align-items-center">
                        {previewMembers.slice(0, 6).map((membership) => (
                            <Avatar
                                key={memberKey(membership)}
                                memberData={memberProfile(membership)}
                                className="text-2xs font-bold border-2 border-white"
                            />
                        ))}
                    </div>
                </div>
            )}
            <div className={`ai-action-wizard-preview-structure ${previewMuted ? "is-muted" : ""}`}>
                <div className="ai-action-wizard-flow-section">
                    <div className="flex align-items-center justify-content-between gap-2">
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-sitemap text-primary"/>
                            <span className="font-semibold text-xs">
                                {translate("ai.creation.preview.generatedFlow")}
                            </span>
                        </div>
                        <Tag className="text-xs" value={translate("ai.actionWizard.fullPreview")}/>
                    </div>
                    <div
                        className="ai-action-wizard-flow"
                        dir="ltr"
                        role="img"
                        aria-label={translate("ai.creation.preview.generatedFlow")}
                    >
                        <ReactFlow
                            key={`${previewOperations.length}-${operationTitle(previewRoot)}`}
                            nodes={graph.nodes}
                            edges={graph.edges}
                            fitView
                            fitViewOptions={{padding: 0.18, minZoom: 0.35, maxZoom: 1}}
                            onInit={(instance) => {
                                window.requestAnimationFrame(() => instance.fitView({
                                    padding: 0.18,
                                    minZoom: 0.35,
                                    maxZoom: 1,
                                }));
                            }}
                            nodesConnectable={false}
                            nodesDraggable={false}
                            elementsSelectable={false}
                            minZoom={0.25}
                            maxZoom={1.5}
                        >
                            <Background gap={18}/>
                            <Controls showInteractive={false}/>
                        </ReactFlow>
                    </div>
                </div>
            </div>
        </div>
    );

    const footerLabel = aiActionLabel({
        entityType: root?.entityType || draft.entityType || creationType,
        actionKey: draft.actionKey || `create_${root?.entityType || creationType}`,
        actionCount: operations.length,
    }, translate);
    const finalReady = proposalOnly
        || creationMode === "manual"
        || Boolean(operations.length);

    return (
        <>
            <ConfirmDialog group={closeDialogGroup} className="rtl"/>
            <ConfirmDialog group={clearDialogGroup} className="rtl"/>
            <Dialog
                visible={visible}
                onHide={requestClose}
                modal
                showHeader={false}
                dismissableMask={false}
                closeOnEscape={!loading && !generating}
                blockScroll
                className="ai-action-wizard rtl"
                pt={{
                    mask: {className: "p-0"},
                    content: {className: "p-0"},
                }}
            >
                <div className="ai-action-wizard-shell">
                    <header className="ai-action-wizard-header">
                        <div className="flex align-items-center gap-3 min-w-0">
                            <BrandLogo className="ai-action-wizard-logo flex-none"/>
                            <span className="ai-action-wizard-header-divider"/>
                            <div className="flex flex-column gap-1 min-w-0">
                                <span className="font-semibold text-sm overflow-hidden text-overflow-ellipsis white-space-nowrap">
                                    {translate("ai.actionWizard.title")}
                                </span>
                                <span className="text-xs text-500 overflow-hidden text-overflow-ellipsis white-space-nowrap">
                                    {workspace?.name || translate("ai.creation.workspaceFallback")}
                                </span>
                            </div>
                        </div>
                        <div className="flex align-items-center gap-2">
                            {!proposalOnly && !externallySeeded && (
                                <Button
                                    type="button"
                                    text
                                    severity="danger"
                                    className="text-xs gap-2"
                                    icon="pi pi-trash"
                                    label={translate("ai.creation.clear.button")}
                                    disabled={loading || generating}
                                    onClick={clearDraft}
                                />
                            )}
                            <Button
                                type="button"
                                text
                                rounded
                                icon="pi pi-times"
                                aria-label={translate("close")}
                                disabled={loading || generating}
                                onClick={requestClose}
                            />
                        </div>
                    </header>
                    <div className="ai-action-wizard-mobile-switch">
                        <Button
                            type="button"
                            text={mobilePane !== "form"}
                            className="flex-1 text-xs gap-2"
                            icon="pi pi-pencil"
                            label={translate("ai.creation.mobile.form")}
                            onClick={() => setMobilePane("form")}
                        />
                        <Button
                            type="button"
                            text={mobilePane !== "preview"}
                            className="flex-1 text-xs gap-2"
                            icon="pi pi-eye"
                            label={translate("ai.creation.mobile.preview")}
                            onClick={() => setMobilePane("preview")}
                        />
                    </div>
                    <div className={`ai-action-wizard-layout is-${mobilePane}`}>
                        <aside className="ai-action-wizard-form">
                            {!proposalOnly && (
                                <Steps
                                    model={stepItems}
                                    activeIndex={activeStep}
                                    readOnly={false}
                                    onSelect={(event) => goToStep(event.index)}
                                    className="ai-action-wizard-steps-nav"
                                />
                            )}
                            <div className="ai-action-wizard-form-scroll">
                                {workspaceStatus !== "active" && (
                                    <Message
                                        severity="warn"
                                        text={translate(`workspace-status.${workspaceStatus}`)}
                                    />
                                )}
                                {storageWarning && (
                                    <Message
                                        severity="warn"
                                        text={translate("ai.creation.storageQuota")}
                                    />
                                )}
                                {!proposalOnly && activeStep === 0 && renderStepOne()}
                                {!proposalOnly && activeStep === 1 && renderStepTwo()}
                                {!proposalOnly && activeStep === 2 && renderStepThree()}
                                {(proposalOnly || activeStep === 3) && renderStepFour()}
                            </div>
                            <footer className="ai-action-wizard-footer">
                                {!proposalOnly && (
                                    <Button
                                        type="button"
                                        outlined
                                        className="text-xs gap-2 flex-1"
                                        icon="pi pi-arrow-right"
                                        label={translate("ai.actionWizard.back")}
                                        disabled={activeStep === 0 || loading || generating}
                                        onClick={() => goToStep(activeStep - 1)}
                                    />
                                )}
                                {!proposalOnly && activeStep < STEPS.length - 1 ? (
                                    <Button
                                        type="button"
                                        className="text-xs gap-2 flex-1"
                                        icon="pi pi-arrow-left"
                                        iconPos="right"
                                        label={translate("ai.actionWizard.next")}
                                        disabled={loading || generating || workspaceStatus !== "active"}
                                        onClick={next}
                                    />
                                ) : (
                                    <Button
                                        type="button"
                                        className="text-xs gap-2 flex-1"
                                        icon={finalReady ? "pi pi-check" : "pi pi-sparkles"}
                                        label={finalReady ? footerLabel : translate("ai.creation.generate")}
                                        loading={loading || generating}
                                        disabled={workspaceStatus !== "active" || (!proposalOnly && !allowedTypes.length)}
                                        onClick={finalReady ? finish : generate}
                                    />
                                )}
                            </footer>
                        </aside>
                        <section className="ai-action-wizard-preview">
                            {renderPreview()}
                        </section>
                    </div>
                </div>
            </Dialog>
        </>
    );
}

export default AiActionWizard;
